import type { AbilityName, AbilityScores } from "../types/entity.js";

/**
 * Ability Score Improvement (W10).
 *
 * Per `specs/world-and-progression.md` the bkcrawl curve grants ASIs at
 * levels 3, 6, and 9 (moved earlier than SRD's 4/8). The player picks one
 * of two shapes:
 *   - `+2` to a single ability
 *   - `+1` to two different abilities
 *
 * Caps at 20 per ability (SRD rule). If a pick would raise an ability
 * past 20 the extra points are dropped and `capped` is true so the UI
 * can surface it.
 */

export type AsiChoice =
  | { readonly kind: "one"; readonly ability: AbilityName }
  | {
      readonly kind: "two";
      readonly first: AbilityName;
      readonly second: AbilityName;
    };

export const ABILITY_CAP = 20;

export interface AsiApplyResult {
  readonly abilities: AbilityScores;
  /** True when at least one point was dropped by the cap. */
  readonly capped: boolean;
}

/**
 * Validate a choice without mutating anything. Returns the first error
 * message, or `null` when the choice is well-formed. UI should disable
 * confirm until this returns `null`.
 */
export function validateAsiChoice(choice: AsiChoice): string | null {
  if (choice.kind === "two" && choice.first === choice.second) {
    return "Pick two different abilities when taking +1/+1.";
  }
  return null;
}

function bump(abilities: AbilityScores, ability: AbilityName, amount: number): {
  abilities: AbilityScores;
  capped: boolean;
} {
  const current = abilities[ability];
  const next = Math.min(ABILITY_CAP, current + amount);
  return {
    abilities: { ...abilities, [ability]: next },
    capped: current + amount > ABILITY_CAP,
  };
}

/** Apply an ASI choice to an `AbilityScores` block. Pure. */
export function applyAsi(abilities: AbilityScores, choice: AsiChoice): AsiApplyResult {
  const invalid = validateAsiChoice(choice);
  if (invalid !== null) {
    return { abilities, capped: false };
  }
  if (choice.kind === "one") {
    const r = bump(abilities, choice.ability, 2);
    return r;
  }
  const first = bump(abilities, choice.first, 1);
  const second = bump(first.abilities, choice.second, 1);
  return { abilities: second.abilities, capped: first.capped || second.capped };
}
